import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';

export interface ConnectionStatus {
  connected: boolean;
  connection: {
    id: number;
    email: string;
    provider: string;
    connectedAt: string;
    lastUsedAt: string | null;
    lastTokenRefreshAt: string | null;
    scopes: string;
    hasError: boolean;
    errorMessage: string | null;
  } | null;
}

interface UseEmailAssistantReturn {
  status: ConnectionStatus | null;
  isLoading: boolean;
  error: string | null;
  connect: () => Promise<string | null>;
  disconnect: () => Promise<void>;
  refreshConnection: () => Promise<void>;
  refetchStatus: () => Promise<void>;
  clearError: () => void;
}

export const useEmailAssistant = (): UseEmailAssistantReturn => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/email-assistant/status`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch connection status');
      }
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('Error fetching email status:', err);
      setError('Failed to load connection status');
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const connect = useCallback(async (): Promise<string | null> => {
    try {
      setError(null);
      const response = await authenticatedFetch(
        `${API_URL}/api/email-assistant/auth/start?return_url=/email-assistant`
      );

      if (!response) {
        setError('Failed to start connection - no response');
        return null;
      }

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 409) {
          setError('You already have an email connected. Disconnect first to reconnect.');
        } else {
          setError(data.error || 'Failed to start connection');
        }
        return null;
      }

      const data = await response.json();
      return data.authUrl;
    } catch (err) {
      console.error('Error starting connection:', err);
      setError('Failed to start email connection');
      return null;
    }
  }, [authenticatedFetch]);

  const disconnect = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const response = await authenticatedFetch(
        `${API_URL}/api/email-assistant/connection`,
        { method: 'DELETE' }
      );

      if (!response || !response.ok) {
        const data = response ? await response.json() : { error: 'No response' };
        throw new Error(data.error || 'Failed to disconnect');
      }

      // Refetch status
      await fetchStatus();
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect email');
      throw err;
    }
  }, [authenticatedFetch, fetchStatus]);

  const refreshConnection = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const response = await authenticatedFetch(
        `${API_URL}/api/email-assistant/refresh`,
        { method: 'POST' }
      );

      if (!response || !response.ok) {
        const data = response ? await response.json() : { error: 'No response' };
        throw new Error(data.error || 'Failed to refresh connection');
      }

      // Refetch status
      await fetchStatus();
    } catch (err) {
      console.error('Error refreshing connection:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh connection');
      throw err;
    }
  }, [authenticatedFetch, fetchStatus]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    status,
    isLoading,
    error,
    connect,
    disconnect,
    refreshConnection,
    refetchStatus: fetchStatus,
    clearError,
  };
};
